<template>
  <modal
    name="modal--waste"
    transition="nice-modal-fade"
    :click-to-close="false"
    :width="350"
    :height="'auto'"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    @closed="closed"
    class="py-10"
  >
    <div
      class="flex flex-col text-black h-full bg-white overflow-hidden w-full justify-between p-4"
    >
      <div class="w-full">
        <div class="text-center text-4xl mb-1 font-extralight tracking-tight">
          {{ editMode ? 'Waste Log' : 'Log Waste' }}
        </div>

        <div class="mt-4">
          <div class="bg-gray-200 rounded-t-lg p-1 flex">
            <button
              class="flex-1 px-3 py-2 rounded-lg focus:outline-none transition-colors duration-200 text-sm"
              :class="[
                activeTab === 'recipe'
                  ? 'bg-white text-gray-800 font-bold shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              ]"
              @click="activeTab = 'recipe'"
            >
              Recipe
            </button>
            <button
              class="flex-1 px-3 py-2 rounded-lg focus:outline-none transition-colors duration-200 text-sm"
              :class="[
                activeTab === 'product'
                  ? 'bg-white text-gray-800 font-bold shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              ]"
              @click="activeTab = 'product'"
            >
              Product
            </button>
          </div>

          <div
            class="bg-white border border-gray-200 rounded-b-lg p-3 shadow-sm"
          >
            <div v-if="activeTab === 'recipe'">
              <label
                class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              >
                Select Recipe
              </label>
              <select
                v-model="selectedRecipe"
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              >
                <option value="" disabled>Select a recipe</option>
                <option v-for="id in sortedRecipeIds" :key="id" :value="id">
                  {{ recipeName(id) }}
                </option>
              </select>

              <div class="mt-2 text-sm text-gray-600">
                <div class="flex justify-between">
                  <div>
                    <span class="font-semibold">Unit Type:</span>
                    {{ selectedRecipeUnit }}
                  </div>
                  <div>
                    <span class="font-semibold">Cost per Unit:</span>
                    {{
                      selectedUnitCost ? `$${selectedUnitCost.toFixed(2)}` : ''
                    }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeTab === 'product'">
              <label
                class="block text-left uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
              >
                Select Product
              </label>
              <select
                v-model="selectedProduct"
                class="block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500"
              >
                <option value="" disabled>Select a product</option>
                <option v-for="id in sortedProductIds" :key="id" :value="id">
                  {{ productName(id) }}
                </option>
              </select>

              <div class="mt-2 text-sm text-gray-600">
                <div class="flex justify-between">
                  <div>
                    <span class="font-semibold">Unit Type:</span>
                    {{
                      activeTab === 'recipe'
                        ? selectedRecipeUnit
                        : genericProductUnitType
                    }}
                  </div>
                  <div>
                    <span class="font-semibold">Cost per Unit:</span>
                    {{
                      selectedUnitCost ? `$${selectedUnitCost.toFixed(2)}` : ''
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Quantity {{ selectedUofM ? `(# of ${selectedUofM})` : '' }}
          <input
            v-if="!showWeightInput && !showVolumeInput"
            v-model.number="rawQuantity"
            type="number"
            min="0"
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2"
          />

          <!-- Weight input (oz-wt) -->
          <div v-if="showWeightInput" class="flex flex-row space-x-2 mt-2">
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Pounds</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                v-model.number="pounds"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Ounces</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="15.99"
                step="0.01"
                v-model.number="ounces"
                autocomplete="off"
              />
            </div>
          </div>

          <!-- Volume input (oz-fl) -->
          <div v-else-if="showVolumeInput" class="flex flex-row space-x-2 mt-2">
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Gallons</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                v-model.number="gallons"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Quarts</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="3"
                v-model.number="quarts"
                autocomplete="off"
              />
            </div>
            <div class="flex-1">
              <label class="block text-xs text-gray-600 mb-1">Ounces</label>
              <input
                class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="number"
                min="0"
                max="31.99"
                step="0.01"
                v-model.number="fluidOunces"
                autocomplete="off"
              />
            </div>
          </div>
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Reason
          <select
            v-model="reason"
            class="text-base font-normal block appearance-none w-full bg-gray-200 border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded leading-tight focus:outline-none focus:shadow-outline focus:bg-white focus:border-gray-500 mt-2"
          >
            <option value="" disabled>Select reason</option>
            <option value="expired">Expired</option>
            <option value="damaged">Damaged</option>
            <option value="quality">Quality Issue</option>
            <option value="overproduction">Overproduction</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Details
          <textarea
            v-model="details"
            rows="2"
            class="text-base font-normal focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500 mt-2 resize-none"
            placeholder="Optional details about the waste"
          ></textarea>
        </label>

        <label
          v-if="editMode"
          class="mt-4 block text-left uppercase tracking-wide text-gray-700 text-xs font-bold"
        >
          Date
          <v-date-picker
            v-model="date"
            :is-required="true"
            :popover="{
              placement: $screens({ default: 'bottom-end', sm: 'bottom' }),
              positionFixed: true,
              visibility: 'click'
            }"
          >
            <template #default="{ inputValue, inputEvents }">
              <input
                class="text-base font-normal mt-2 py-3 px-4 focus:outline-none focus:shadow-outline appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                type="text"
                :value="inputValue"
                v-on="inputEvents"
                readonly
              />
            </template>
          </v-date-picker>
        </label>

        <div class="mt-6 flex flex-row justify-between">
          <button
            class="buttonOutline focus:shadow-outline focus:outline-none"
            type="button"
            @click="$modal.hide('modal--waste')"
          >
            Cancel
          </button>
          <button
            class="ml-3 button focus:shadow-outline focus:outline-none"
            type="button"
            :disabled="!isValid"
            @click="submit"
          >
            {{ editMode ? 'Update' : 'Log' }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import VDatePicker from 'v-calendar/lib/components/date-picker.umd'

export default {
  name: 'ModalWaste',
  components: {
    VDatePicker
  },
  data() {
    return {
      activeTab: 'recipe',
      selectedRecipe: '',
      selectedProduct: '',
      rawQuantity: 1, // Use rawQuantity for the raw value
      reason: '',
      details: '',
      date: new Date(),
      editMode: false,
      editId: null,
      pounds: 0,
      ounces: 0,
      gallons: 0,
      quarts: 0,
      fluidOunces: 0
    }
  },
  computed: {
    ...mapGetters('global', [
      'products',
      'productName',
      'productById',
      'isProduct'
    ]),
    ...mapGetters('storeModule/products', [
      'productByGenericId',
      'productIdByGenericId'
    ]),
    ...mapGetters('recipes', [
      'recipes',
      'recipeIds',
      'recipeName',
      'recipeUnitType',
      'recipeById',
      'isRecipe'
    ]),
    ...mapGetters('storeModule', ['id', 'recipeUnitCostById']),
    ...mapGetters('storeModule/waste', ['wasteById']),

    sortedRecipeIds() {
      if (!this.recipes) return []
      return Object.keys(this.recipes).sort((a, b) => {
        const nameA = (this.recipeName(a) || '').toLowerCase()
        const nameB = (this.recipeName(b) || '').toLowerCase()
        return nameA.localeCompare(nameB)
      })
    },
    sortedProductIds() {
      if (!this.products) return []
      return Object.keys(this.products)
        .filter((id) => !this.products[id].hidden)
        .sort((a, b) => {
          const nameA = (this.productName(a) || '').toLowerCase()
          const nameB = (this.productName(b) || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
    },
    selectedUofM() {
      if (this.activeTab === 'recipe') {
        return this.recipeUnitType(this.selectedRecipe) || ''
      } else {
        if (!this.selectedProduct) return ''
        const product = this.productById(this.selectedProduct)
        return product ? product.unitRecipe || '' : ''
      }
    },

    showWeightInput() {
      if (this.activeTab === 'recipe' && this.selectedRecipe) {
        const recipe = this.recipeById(this.selectedRecipe)
        return recipe && recipe.unitType === 'oz-wt'
      }
      return false
    },

    showVolumeInput() {
      if (this.activeTab === 'recipe' && this.selectedRecipe) {
        const recipe = this.recipeById(this.selectedRecipe)
        return recipe && recipe.unitType === 'oz-fl'
      }
      return false
    },

    // Calculate the final quantity value based on unit type
    quantity() {
      console.log('Computing waste quantity value')
      if (this.showWeightInput) {
        const total = (this.pounds || 0) * 16 + parseFloat(this.ounces || 0)
        console.log(
          `Weight calculation: ${this.pounds} pounds and ${this.ounces} ounces = ${total} total ounces`
        )
        return total
      } else if (this.showVolumeInput) {
        const total =
          (this.gallons || 0) * 128 +
          (this.quarts || 0) * 32 +
          parseFloat(this.fluidOunces || 0)
        console.log(
          `Volume calculation: ${this.gallons} gallons, ${this.quarts} quarts, and ${this.fluidOunces} fluid ounces = ${total} total fluid ounces`
        )
        return total
      } else {
        console.log(`Standard quantity: ${this.rawQuantity}`)
        return this.rawQuantity || 1
      }
    },
    isValid() {
      return (
        ((this.activeTab === 'recipe' && this.selectedRecipe) ||
          (this.activeTab === 'product' && this.selectedProduct)) &&
        this.quantity > 0 &&
        this.reason
      )
    },
    selectedUnitCost() {
      if (this.activeTab === 'recipe') {
        if (!this.selectedRecipe) return 0
        return this.recipeUnitCostById(this.selectedRecipe) || 0
      } else {
        if (!this.selectedProduct) return 0
        const localProductId = this.productIdByGenericId(this.selectedProduct)
        const product = this.productById(this.selectedProduct)
        if (!product || !localProductId) return 0

        const productPrice =
          this.$store.getters['storeModule/productPriceByProductId'](
            localProductId
          )
        return productPrice ? productPrice.pricePerCountingUnit || 0 : 0
      }
    },
    genericProductUnitType() {
      if (!this.selectedProduct) return ''
      const product = this.productById(this.selectedProduct)
      return product ? product.unitRecipe || '' : ''
    },
    selectedRecipeUnit() {
      return this.recipeUnitType(this.selectedRecipe) || ''
    }
  },
  methods: {
    ...mapActions('storeModule/waste', ['createWaste', 'updateWaste']),
    ...mapActions('config', ['setFabVisible']),

    beforeOpen(event) {
      this.setFabVisible(false)
      if (event.params && event.params.wasteId) {
        // Edit mode
        this.editMode = true
        this.editId = event.params.wasteId
        const waste = this.wasteById(event.params.wasteId)

        if (!waste) {
          console.error('Could not find waste with ID:', event.params.wasteId)
          return
        }

        // Populate form with existing data
        this.activeTab = waste.itemType
        this.selectedRecipe = waste.itemType === 'recipe' ? waste.itemId : ''
        this.selectedProduct = waste.itemType === 'product' ? waste.itemId : ''

        // Set quantity after recipe/product ID to ensure computed properties are updated
        this.rawQuantity = waste.quantity || 1

        // Convert ounces to compound units if needed
        if (waste.itemType === 'recipe') {
          // Wait for next tick to ensure recipe is loaded
          this.$nextTick(() => {
            if (this.showWeightInput) {
              this.pounds = Math.floor(waste.quantity / 16)
              this.ounces = parseFloat((waste.quantity % 16).toFixed(2))
              console.log(
                `Editing waste: converted ${waste.quantity} oz to ${this.pounds} pounds and ${this.ounces} ounces`
              )
            } else if (this.showVolumeInput) {
              this.gallons = Math.floor(waste.quantity / 128)
              const remainingOz = waste.quantity % 128
              this.quarts = Math.floor(remainingOz / 32)
              this.fluidOunces = parseFloat((remainingOz % 32).toFixed(2))
              console.log(
                `Editing waste: converted ${waste.quantity} fl oz to ${this.gallons} gallons, ${this.quarts} quarts, and ${this.fluidOunces} fluid ounces`
              )
            }
          })
        }

        this.reason = waste.reason
        this.details = waste.details || ''

        // Handle date conversion with explicit null check
        this.date =
          waste.date && waste.date.seconds
            ? new Date(waste.date.seconds * 1000)
            : new Date()
      } else {
        this.resetForm()
      }
    },

    beforeClose() {
      this.setFabVisible(true)
      this.editMode = false
      this.editId = null
      this.resetForm()
    },

    closed() {
      this.setFabVisible(true)
      this.resetForm()
    },

    resetForm() {
      this.editMode = false
      this.editId = null
      this.activeTab = 'recipe'
      this.selectedRecipe = ''
      this.selectedProduct = ''
      this.rawQuantity = 1
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.reason = ''
      this.details = ''
      this.date = new Date()
    },

    async submit() {
      if (!this.isValid) return // Early validation check

      // Calculate the final quantity value based on unit type
      let finalQuantity = this.quantity
      console.log(`Submitting waste with final quantity: ${finalQuantity}`)

      const data = {
        itemType: this.activeTab,
        itemId:
          this.activeTab === 'recipe'
            ? this.selectedRecipe
            : this.selectedProduct,
        itemName:
          this.activeTab === 'recipe'
            ? this.recipeName(this.selectedRecipe)
            : this.productName(this.selectedProduct),
        quantity: finalQuantity,
        unitType: this.selectedUofM,
        reason: this.reason,
        notes: this.details || '',
        date: this.date,
        unitCost: this.selectedUnitCost,
        totalCost: this.selectedUnitCost * finalQuantity
      }

      if (this.activeTab === 'product') {
        data.productId = this.productIdByGenericId(this.selectedProduct)
      }

      try {
        if (this.editMode) {
          await this.updateWaste({ id: this.editId, ...data })
        } else {
          await this.createWaste(data)
        }
        this.$modal.hide('modal--waste')
      } catch (error) {
        console.error('Error saving waste:', error)
        this.$modal.show('modal--message', {
          title: 'Error',
          message: 'Failed to save waste log'
        })
      }
    }
  },
  watch: {
    // Watch for recipe changes to update unit display
    selectedRecipe() {
      // Reset compound values when recipe changes
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.rawQuantity = 1
    },

    activeTab() {
      // Reset compound values when tab changes
      this.pounds = 0
      this.ounces = 0
      this.gallons = 0
      this.quarts = 0
      this.fluidOunces = 0
      this.rawQuantity = 1
    },

    // Add watchers for the compound unit inputs to help with debugging
    pounds() {
      console.log(
        `Pounds changed to ${this.pounds}, total ounces now: ${this.quantity}`
      )
    },
    ounces() {
      console.log(
        `Ounces changed to ${this.ounces}, total ounces now: ${this.quantity}`
      )
    },
    gallons() {
      console.log(
        `Gallons changed to ${this.gallons}, total fluid ounces now: ${this.quantity}`
      )
    },
    quarts() {
      console.log(
        `Quarts changed to ${this.quarts}, total fluid ounces now: ${this.quantity}`
      )
    },
    fluidOunces() {
      console.log(
        `Fluid ounces changed to ${this.fluidOunces}, total fluid ounces now: ${this.quantity}`
      )
    }
  }
}
</script>
